import type {
    DriverInfo,
    LoadInIframeResult,
    LoadInSourcelessIframeResult,
    OfficeWebHostScope,
} from '@ms/office-web-host';
import { PerformanceDatapoint } from 'owa-analytics';
import { assertNever } from 'owa-assert';
import { getSessionId } from 'owa-config';
import { timestamp } from 'owa-datetime';
import { onFluidFileLoadFailure } from 'owa-fluid-link';
import {
    fluidComponentCancelled,
    fluidComponentError,
    fluidComponentLoaded,
} from 'owa-fluid-datapoints';
import {
    FluidLoadOwaScenario,
    FluidLoadSource,
    FluidOwaSource,
    FluidOwaScenario,
} from 'owa-fluid-validations';
import * as trace from 'owa-trace';
import ReactDOM from 'react-dom';
import { ContentPosition, IEditor } from 'roosterjs-editor-types';
import { getDriverDetailsForFluidItem } from './getDriverDetailsForFluidItem';
import { createFluidTargetAndHeader } from './createFluidTargetAndHeader';
import { renderFluidComponent } from '../../components/FluidComponent';
import { getFluidPropsForRender } from '../fluidProps/getFluidPropsForRender';
import {
    getNotificationServicePromise,
    getShareServicePromise,
} from '../fluidProps/hostContext/scopeHelpers';
import { FluidOwaScenarioNames } from '../constants';
import { CancellationError } from '@fluidx/office-fluid-types';

// Mounts a pasted fluid component in an editor.
// Handles clean up and insertion
export async function mountPastedFluidComponent(
    editor: IEditor,
    originalNode: Node,
    driverInfo: DriverInfo,
    url: string,
    scenarioStartTime: number,
    owaSource: FluidOwaSource,
    addRecipientFunc: (address: string) => void,
    viewStateId?: string
) {
    const perfDatapoint = new PerformanceDatapoint('FluidMountPastedComponent');
    const scenario: FluidOwaScenario = getScenarioFromSource(owaSource);
    const scenarioName: string = FluidOwaScenarioNames[scenario];
    perfDatapoint.addCustomData({
        differenceFromStartTime: timestamp() - scenarioStartTime,
        scenario: scenarioName,
    });

    const [targetDiv, headerNode] = createFluidTargetAndHeader(
        editor,
        undefined /* containerNode */,
        originalNode
    );

    // Get OWA's session Id from config
    const sessionId = getSessionId();
    const removeElementCallback = () => {
        ReactDOM.unmountComponentAtNode(targetDiv);
    };
    const mountComponentProps = getFluidPropsForRender(
        sessionId,
        scenarioName,
        scenarioStartTime,
        owaSource,
        undefined /* codeDetails */,
        url,
        removeElementCallback
    );

    let result: LoadInIframeResult | null = null;
    try {
        // We need to resolve the driverDetails for an already existing fluid component
        // after which we can instantiate the scope object.
        const driverDetails = getDriverDetailsForFluidItem(driverInfo.documentUrl);
        perfDatapoint.addCheckmark('fetchedDriverDetails');
        if (!driverDetails) {
            trace.errorThatWillCauseAlert(
                'Could not create driver details for existing fluid item'
            );
            throw new Error('failed to create driver details');
        }

        const scope: OfficeWebHostScope = {
            NotificationServicePromise: getNotificationServicePromise(
                mountComponentProps.tokenProvider,
                driverDetails.itemId,
                driverDetails.driveId,
                driverDetails.siteUrl,
                mountComponentProps.hostConfig.logger,
                sessionId,
                addRecipientFunc
            ),
            ShareServicePromise: getShareServicePromise(
                mountComponentProps.tokenProvider,
                mountComponentProps.hostConfig.logger,
                driverDetails.itemId,
                driverDetails.driveId,
                driverDetails.siteUrl,
                addRecipientFunc
            ),
            StylingProvider: mountComponentProps.stylingProvider,
            PresenceColorProvider: mountComponentProps.presenceColorProvider,
            HostContext: mountComponentProps.hostContext,
            TokenProvider: mountComponentProps.tokenProvider,
            CommandingSurfaceViewProvider: mountComponentProps.commandingSurfaceParts.viewProvider,
        };

        result = await new Promise(resolve => {
            renderFluidComponent(
                mountComponentProps,
                scope,
                targetDiv,
                driverInfo,
                headerNode,
                resolve,
                owaSource,
                false // isCreateNew
            );
        });
        const loadedInfo = await result!.loadedInfo;
        perfDatapoint.addCheckmark('fetchedLoadedInfo');
        await Promise.all([loadedInfo.component, loadedInfo.container]);
        perfDatapoint.addCheckmark('componentAndContainerResolved');

        fluidComponentLoaded(
            FluidLoadOwaScenario.ComposeWithExistingComponent,
            owaSource,
            FluidLoadSource.Bohemia,
            scenarioStartTime
        );
        perfDatapoint.end();
    } catch (error) {
        // We re-add the originalNode, since it was taken into the header in disPlayHeader above,
        // which we will remove.
        if (!editor.isDisposed()) {
            const range = editor.getDocument().createRange();
            range.selectNode(targetDiv);
            range.collapse();
            editor.insertNode(originalNode, { position: ContentPosition.Range, range });

            editor.deleteNode(targetDiv);
        }
        // If the getIframe method is defined, but it returns undefined that means that the component has been unmounted or disposed
        // In this case we should treat any error like a cancellation error.
        const isIFrameUnmounted =
            (result as LoadInSourcelessIframeResult)?.getIframe &&
            !(result as LoadInSourcelessIframeResult).getIframe();
        if (error instanceof CancellationError || isIFrameUnmounted) {
            fluidComponentCancelled(
                FluidLoadOwaScenario.ComposeWithExistingComponent,
                owaSource,
                scenarioStartTime
            );
            perfDatapoint.invalidate();
            return;
        }
        onFluidFileLoadFailure(owaSource, error, viewStateId);
        fluidComponentError(
            FluidLoadOwaScenario.ComposeWithExistingComponent,
            owaSource,
            FluidLoadSource.Bohemia,
            error
        );
        perfDatapoint.endWithError(error);
    }
}

function getScenarioFromSource(source: FluidOwaSource): FluidOwaScenario {
    switch (source) {
        case FluidOwaSource.MailCompose:
            return FluidOwaScenario.MAIL_COMPONENT_PASTE;
        case FluidOwaSource.AgendaEditor:
        case FluidOwaSource.CalendarCompose:
            return FluidOwaScenario.CALENDAR_COMPONENT_PASTE;
        case FluidOwaSource.MailReadingPane:
        case FluidOwaSource.MailCalendarCard:
        case FluidOwaSource.CalendarReadingPane:
        case FluidOwaSource.Timestream:
        case FluidOwaSource.None:
            trace.errorThatWillCauseAlert(
                `Incompatible fluid source for pasted fluid component. Source is ${source}`
            );
            return FluidOwaScenario.UNKNOWN_COMPONENT_PASTE;
        default:
            return assertNever(source);
    }
}
